import { useState, useCallback } from "react";
import { useLocation } from "wouter";
import { Plus, Pencil, Trash2, Star, StarOff, ChevronDown, ChevronUp, X, ArrowUp, ArrowDown, Check, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useApartments, useQaOrgSettings, useUpdateQaOrgSettings } from "@/hooks/use-resources";
import {
  useQaTemplates,
  useCreateQaTemplate,
  useUpdateQaTemplate,
  useDeleteQaTemplate,
  useSetDefaultQaTemplate,
  useApplyQaTemplate,
  useQaCriteria,
} from "@/hooks/use-resources";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ZoneCriteriaForm {
  requiredItems: string;
  cleanlinessNotes: string;
  stagingNotes: string;
  amenityNotes: string;
}

interface ZoneForm {
  zoneName: string;
  zoneOrder: number;
  criteria: ZoneCriteriaForm;
  expanded: boolean;
}

interface TemplateForm {
  name: string;
  zones: ZoneForm[];
  isDefault: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatZoneLabel(zoneName: string): string {
  return zoneName.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function emptyCriteria(): ZoneCriteriaForm {
  return { requiredItems: "", cleanlinessNotes: "", stagingNotes: "", amenityNotes: "" };
}

function zoneToForm(zone: any): ZoneForm {
  return {
    zoneName: zone.zoneName,
    zoneOrder: zone.zoneOrder,
    criteria: {
      requiredItems: zone.criteria?.requiredItems?.join(", ") ?? "",
      cleanlinessNotes: zone.criteria?.cleanlinessNotes ?? "",
      stagingNotes: zone.criteria?.stagingNotes ?? "",
      amenityNotes: zone.criteria?.amenityNotes ?? "",
    },
    expanded: false,
  };
}

function zoneFormToData(zone: ZoneForm): { zoneName: string; zoneOrder: number; criteria: any } {
  const criteria: Record<string, any> = {};
  const items = zone.criteria.requiredItems.split(",").map(s => s.trim()).filter(Boolean);
  if (items.length) criteria.requiredItems = items;
  if (zone.criteria.cleanlinessNotes.trim()) criteria.cleanlinessNotes = zone.criteria.cleanlinessNotes.trim();
  if (zone.criteria.stagingNotes.trim()) criteria.stagingNotes = zone.criteria.stagingNotes.trim();
  if (zone.criteria.amenityNotes.trim()) criteria.amenityNotes = zone.criteria.amenityNotes.trim();
  return {
    zoneName: zone.zoneName,
    zoneOrder: zone.zoneOrder,
    criteria: Object.keys(criteria).length > 0 ? criteria : null,
  };
}

// ── Zone editor within template dialog ───────────────────────────────────────

function ZoneEditor({ zone, index, total, onChange, onRemove, onMove }: {
  zone: ZoneForm;
  index: number;
  total: number;
  onChange: (z: ZoneForm) => void;
  onRemove: () => void;
  onMove: (dir: "up" | "down") => void;
}) {
  return (
    <div className="border rounded-md">
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="flex flex-col gap-0.5">
          <button type="button" onClick={() => onMove("up")} disabled={index === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-20">
            <ArrowUp className="w-3 h-3" />
          </button>
          <button type="button" onClick={() => onMove("down")} disabled={index === total - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-20">
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
        <span className="flex-1 text-sm font-medium">{formatZoneLabel(zone.zoneName) || <span className="text-gray-400">unnamed zone</span>}</span>
        <button type="button" onClick={() => onChange({ ...zone, expanded: !zone.expanded })} className="text-gray-400 hover:text-gray-600">
          {zone.expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <button type="button" onClick={onRemove} className="text-gray-400 hover:text-red-500">
          <X className="w-4 h-4" />
        </button>
      </div>
      {zone.expanded && (
        <div className="px-3 pb-3 space-y-2 border-t pt-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Zone name (slug)</label>
            <Input
              value={zone.zoneName}
              onChange={e => onChange({ ...zone, zoneName: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
              placeholder="e.g. master_bedroom"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Required items (comma-separated)</label>
            <Input
              value={zone.criteria.requiredItems}
              onChange={e => onChange({ ...zone, criteria: { ...zone.criteria, requiredItems: e.target.value } })}
              placeholder="e.g. toilet paper, hand soap, 2 towels"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Cleanliness notes</label>
            <Textarea
              value={zone.criteria.cleanlinessNotes}
              onChange={e => onChange({ ...zone, criteria: { ...zone.criteria, cleanlinessNotes: e.target.value } })}
              placeholder="e.g. Grout must be clean, no hair on floor"
              className="text-sm resize-none"
              rows={2}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Staging notes</label>
            <Textarea
              value={zone.criteria.stagingNotes}
              onChange={e => onChange({ ...zone, criteria: { ...zone.criteria, stagingNotes: e.target.value } })}
              placeholder="e.g. 4 pillows stacked, throw blanket folded on chair"
              className="text-sm resize-none"
              rows={2}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Amenity notes</label>
            <Textarea
              value={zone.criteria.amenityNotes}
              onChange={e => onChange({ ...zone, criteria: { ...zone.criteria, amenityNotes: e.target.value } })}
              placeholder="e.g. Welcome card on nightstand, water bottles in fridge"
              className="text-sm resize-none"
              rows={2}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Template editor dialog ────────────────────────────────────────────────────

function TemplateEditorDialog({ open, onClose, initial }: {
  open: boolean;
  onClose: () => void;
  initial: { id?: number; form: TemplateForm } | null;
}) {
  const { toast } = useToast();
  const createTemplate = useCreateQaTemplate();
  const updateTemplate = useUpdateQaTemplate();

  const [form, setForm] = useState<TemplateForm>(
    initial?.form ?? { name: "", zones: [], isDefault: false }
  );
  const [newZoneName, setNewZoneName] = useState("");

  // Reset form when dialog opens
  const handleOpen = useCallback(() => {
    if (initial) setForm(initial.form);
    else setForm({ name: "", zones: [], isDefault: false });
    setNewZoneName("");
  }, [initial]);

  const addZone = () => {
    const slug = newZoneName.trim().toLowerCase().replace(/\s+/g, "_");
    if (!slug) return;
    setForm(f => ({
      ...f,
      zones: [...f.zones, { zoneName: slug, zoneOrder: f.zones.length, criteria: emptyCriteria(), expanded: true }],
    }));
    setNewZoneName("");
  };

  const moveZone = (index: number, dir: "up" | "down") => {
    setForm(f => {
      const zones = [...f.zones];
      const swap = dir === "up" ? index - 1 : index + 1;
      [zones[index], zones[swap]] = [zones[swap], zones[index]];
      return { ...f, zones: zones.map((z, i) => ({ ...z, zoneOrder: i })) };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "Template name required", variant: "destructive" });
      return;
    }
    const zonesJson = form.zones.map(zoneFormToData);
    try {
      if (initial?.id) {
        await updateTemplate.mutateAsync({ id: initial.id, name: form.name, zonesJson, isDefault: form.isDefault });
      } else {
        await createTemplate.mutateAsync({ name: form.name, zonesJson, isDefault: form.isDefault });
      }
      toast({ title: `Template ${initial?.id ? "updated" : "created"}` });
      onClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const isPending = createTemplate.isPending || updateTemplate.isPending;

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); else handleOpen(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit Template" : "New Zone Template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium block mb-1">Template name</label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Standard 2BR Beach Unit"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="is-default"
              checked={form.isDefault}
              onCheckedChange={v => setForm(f => ({ ...f, isDefault: !!v }))}
            />
            <label htmlFor="is-default" className="text-sm cursor-pointer">
              Set as default template (applied to new apartments automatically)
            </label>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium block">Zones ({form.zones.length})</label>
            {form.zones.length === 0 && (
              <p className="text-sm text-gray-400 py-2 text-center">No zones added yet.</p>
            )}
            {form.zones.map((zone, i) => (
              <ZoneEditor
                key={i}
                zone={zone}
                index={i}
                total={form.zones.length}
                onChange={z => setForm(f => { const zones = [...f.zones]; zones[i] = z; return { ...f, zones }; })}
                onRemove={() => setForm(f => ({ ...f, zones: f.zones.filter((_, idx) => idx !== i).map((z, j) => ({ ...z, zoneOrder: j })) }))}
                onMove={dir => moveZone(i, dir)}
              />
            ))}
            <div className="flex gap-2 pt-1">
              <Input
                value={newZoneName}
                onChange={e => setNewZoneName(e.target.value)}
                placeholder="Add zone (e.g. bathroom, kitchen)"
                className="h-8 text-sm"
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addZone(); } }}
              />
              <Button type="button" variant="outline" size="sm" onClick={addZone} className="shrink-0">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving…" : "Save Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Apply template dialog ─────────────────────────────────────────────────────

function ApplyTemplateDialog({ open, onClose, template }: {
  open: boolean;
  onClose: () => void;
  template: any | null;
}) {
  const { toast } = useToast();
  const { data: apartments = [] } = useApartments();
  const applyTemplate = useApplyQaTemplate();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");

  const filtered = apartments.filter((a: any) =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((a: any) => a.id)));
  };

  const handleApply = async () => {
    if (!template || selected.size === 0) return;
    try {
      const result = await applyTemplate.mutateAsync({ templateId: template.id, unitIds: Array.from(selected) });
      toast({ title: `Applied to ${result.applied} unit${result.applied !== 1 ? "s" : ""}` });
      onClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { onClose(); setSelected(new Set()); setSearch(""); } }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply "{template?.name}" to units</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            This will replace existing zone configuration for selected units.
          </p>
          <Input
            placeholder="Search units…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
          <div className="flex items-center gap-2 pb-1">
            <Checkbox
              checked={filtered.length > 0 && selected.size === filtered.length}
              onCheckedChange={toggleAll}
              id="select-all"
            />
            <label htmlFor="select-all" className="text-xs text-gray-500 cursor-pointer">
              Select all ({filtered.length})
            </label>
          </div>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {filtered.map((apt: any) => (
              <div key={apt.id} className="flex items-center gap-2 py-1">
                <Checkbox
                  checked={selected.has(apt.id)}
                  onCheckedChange={v => setSelected(s => { const n = new Set(s); if (v) n.add(apt.id); else n.delete(apt.id); return n; })}
                  id={`apt-${apt.id}`}
                />
                <label htmlFor={`apt-${apt.id}`} className="text-sm cursor-pointer flex-1">{apt.name}</label>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleApply} disabled={selected.size === 0 || applyTemplate.isPending}>
            {applyTemplate.isPending ? "Applying…" : `Apply to ${selected.size} unit${selected.size !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminQaSettings() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { data: templates = [], isLoading } = useQaTemplates();
  const deleteTemplate = useDeleteQaTemplate();
  const setDefault = useSetDefaultQaTemplate();
  const { data: qaSettings, isLoading: settingsLoading } = useQaOrgSettings();
  const updateQaSettings = useUpdateQaOrgSettings();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{ id?: number; form: TemplateForm } | null>(null);
  const [applyTarget, setApplyTarget] = useState<any | null>(null);

  // Local state for settings form
  const [settingsForm, setSettingsForm] = useState<{
    passThreshold: number;
    rescanLimit: number;
    requireAllZones: boolean;
    allowOverrideFailed: boolean;
    autoMaintenance: boolean;
  } | null>(null);

  // Sync settings form from server data
  const currentSettings = settingsForm ?? qaSettings ?? {
    passThreshold: 75,
    rescanLimit: 3,
    requireAllZones: true,
    allowOverrideFailed: true,
    autoMaintenance: false,
  };

  const openNew = () => {
    setEditTarget(null);
    setEditorOpen(true);
  };

  const openEdit = (t: any) => {
    setEditTarget({
      id: t.id,
      form: {
        name: t.name,
        isDefault: t.isDefault ?? false,
        zones: (t.zonesJson ?? []).map(zoneToForm),
      },
    });
    setEditorOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTemplate.mutateAsync(id);
      toast({ title: "Template deleted" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await setDefault.mutateAsync(id);
      toast({ title: "Default template updated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleSaveSettings = async () => {
    try {
      await updateQaSettings.mutateAsync(currentSettings);
      setSettingsForm(null);
      toast({ title: "Inspection settings saved" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const updateSetting = <K extends keyof typeof currentSettings>(key: K, value: typeof currentSettings[K]) => {
    setSettingsForm({ ...currentSettings, [key]: value });
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => setLocation("/admin")} className="h-8 px-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">QA Vision</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage zone templates, inspection criteria, and inspection behavior.
          </p>
        </div>
      </div>

      {/* Inspection Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Inspection Settings</CardTitle>
          <CardDescription>Configure how the AI inspection behaves for your organization.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {settingsLoading ? (
            <p className="text-sm text-gray-400 py-2 text-center">Loading…</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Pass threshold</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Minimum score (%) for an inspection to pass.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    className="w-20 h-8 text-sm text-right"
                    value={currentSettings.passThreshold}
                    onChange={e => updateSetting("passThreshold", Math.max(1, Math.min(100, Number(e.target.value))))}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Re-scan limit</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Max times a cleaner can re-scan a failed zone per job.</p>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  className="w-20 h-8 text-sm text-right"
                  value={currentSettings.rescanLimit}
                  onChange={e => updateSetting("rescanLimit", Math.max(0, Math.min(10, Number(e.target.value))))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="require-all-zones" className="text-sm font-medium">Require all zones</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Inspection can't complete until every zone is scanned.</p>
                </div>
                <Switch
                  id="require-all-zones"
                  checked={currentSettings.requireAllZones}
                  onCheckedChange={v => updateSetting("requireAllZones", v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allow-override" className="text-sm font-medium">Allow override on failed</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Let supervisor mark a failed inspection as passed manually.</p>
                </div>
                <Switch
                  id="allow-override"
                  checked={currentSettings.allowOverrideFailed}
                  onCheckedChange={v => updateSetting("allowOverrideFailed", v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-maintenance" className="text-sm font-medium">Auto-create maintenance ticket</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Automatically open a ticket when an inspection fails.</p>
                </div>
                <Switch
                  id="auto-maintenance"
                  checked={currentSettings.autoMaintenance}
                  onCheckedChange={v => updateSetting("autoMaintenance", v)}
                />
              </div>

              {settingsForm && (
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => setSettingsForm(null)}>Cancel</Button>
                  <Button size="sm" onClick={handleSaveSettings} disabled={updateQaSettings.isPending}>
                    {updateQaSettings.isPending ? "Saving…" : "Save Settings"}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Zone Templates */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">Zone Templates</CardTitle>
            <CardDescription className="mt-0.5">Define which zones get scanned and what the AI checks for.</CardDescription>
          </div>
          <Button size="sm" onClick={openNew}>
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>}
          {!isLoading && templates.length === 0 && (
            <div className="text-center py-8 space-y-2">
              <p className="text-sm text-gray-500">No templates yet.</p>
              <p className="text-xs text-gray-400">
                Create a template to standardize zones across your units. Without a template, zones are auto-generated from bedroom/bathroom count.
              </p>
              <Button variant="outline" size="sm" onClick={openNew} className="mt-2">
                <Plus className="w-4 h-4 mr-1" /> Create your first template
              </Button>
            </div>
          )}
          <div className="space-y-3">
            {templates.map((t: any) => (
              <div key={t.id} className="flex items-center gap-3 border rounded-lg px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{t.name}</span>
                    {t.isDefault && <Badge className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">Default</Badge>}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {(t.zonesJson ?? []).length} zone{(t.zonesJson ?? []).length !== 1 ? "s" : ""}
                    {(t.zonesJson ?? []).length > 0 && (
                      <span> — {(t.zonesJson as any[]).map((z: any) => formatZoneLabel(z.zoneName)).join(", ")}</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setApplyTarget(t)}
                    className="text-xs h-7 px-2"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Apply
                  </Button>
                  {!t.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(t.id)}
                      className="text-xs h-7 px-2 text-gray-500"
                    >
                      <StarOff className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {t.isDefault && (
                    <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-blue-500 cursor-default">
                      <Star className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="h-7 px-2">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(t.id)}
                    className="h-7 px-2 text-red-500 hover:text-red-700"
                    disabled={deleteTemplate.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <TemplateEditorDialog
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditTarget(null); }}
        initial={editTarget}
      />
      <ApplyTemplateDialog
        open={!!applyTarget}
        onClose={() => setApplyTarget(null)}
        template={applyTarget}
      />
    </div>
  );
}
